import React, { useState } from 'react';
import { X, CalendarDays, UserCircle2, ChevronRight } from 'lucide-react';
import { useOnLeaveToday } from '../../../../hooks/manager_portal_hooks';
import TeamMemberDetailModal from '../../TeamMemberDetailModal';

/**
 * On Leave Today Modal
 *
 * Opened from the "On leave today" card on the Manager Dashboard. Lists the
 * manager's team members with an approved leave request covering today (sourced
 * from `useOnLeaveToday`, which derives from the same `useTeamLeaveRequests`
 * query the Approvals tab uses — so no additional API call is made here).
 *
 * Each row shows the member's name and leave type; selecting a member opens
 * the shared TeamMemberDetailModal, keeping the drill-down consistent with the
 * other dashboard modals and the "My Team" tab.
 */
const OnLeaveTodayModal = ({ onClose }) => {
  const { onLeaveToday, isLoading, error } = useOnLeaveToday();
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="On leave today"
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <CalendarDays className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">On Leave Today</h3>
                <p className="text-sm text-gray-500">
                  {isLoading
                    ? 'Loading…'
                    : `${onLeaveToday.length} team member${onLeaveToday.length === 1 ? '' : 's'} on leave`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable list */}
          <div className="overflow-y-auto flex-1 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
                <p className="ml-3 text-gray-600 text-sm">Loading team members on leave…</p>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-red-500 text-sm">Failed to load leave data. Please try again.</p>
              </div>
            ) : onLeaveToday.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500 text-sm">No team members are on leave today.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {onLeaveToday.map((member) => {
                  return (
                    <li key={member.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedMember(member)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-yellow-200 hover:bg-yellow-50/40 transition-colors text-left"
                      >
                        <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                          <UserCircle2 className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {member.leaveTypeName || 'On Leave'}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 bg-yellow-50 text-yellow-700 ring-yellow-200">
                          On Leave
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {selectedMember && (
        <TeamMemberDetailModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </>
  );
};

export default OnLeaveTodayModal;
